import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  styled,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_PRIMARY_HOVER_BG,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../epocus-palette";
import { epocusTheme } from "../epocus-theme-provider";
import { REVIEWERS } from "../reviewers";

// ─── WorksheetSection ────────────────────────────────────────────────────────

const SectionCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const SectionLabel = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

export const WorksheetSection: FCwC<{ label?: string }> = ({
  label,
  children,
}) => (
  <SectionCard>
    {label && <SectionLabel>{label}</SectionLabel>}
    {children}
  </SectionCard>
);

// ─── Shared chip row layout ─────────────────────────────────────────────────

const ChipRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
});

const CHIP_BASE_STYLE: React.CSSProperties = {
  minHeight: 44,
  minWidth: 44,
  fontSize: rem(14),
  borderColor: EPOCUS_BORDER,
};

const chipStyle = (selected: boolean): React.CSSProperties => ({
  ...CHIP_BASE_STYLE,
  fontWeight: selected ? 600 : 400,
  color: selected ? EPOCUS_WHITE : EPOCUS_TEXT_PRIMARY,
  backgroundColor: selected ? EPOCUS_PRIMARY : "transparent",
  borderColor: selected ? EPOCUS_PRIMARY : EPOCUS_BORDER,
});

// ─── IndicationChips ─────────────────────────────────────────────────────────

type ChipOption = { value: string; label: string };

export const IndicationChips: FC<{
  options: ChipOption[];
  selected: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
}> = ({ options, selected, onChange, disabled }) => (
  <ChipRow>
    {options.map((o) => (
      <Chip
        key={o.value}
        label={o.label}
        variant="outlined"
        style={chipStyle(selected === o.value)}
        onClick={() => onChange(selected === o.value ? null : o.value)}
        disabled={disabled}
      />
    ))}
  </ChipRow>
);

// ─── EnumChipSelect ──────────────────────────────────────────────────────────

export const EnumChipSelect = IndicationChips;

// ─── ViewChecklist (tap-to-toggle rows with circle indicators) ──────────────

const ViewRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  borderRadius: 8,
  border: `1px solid ${EPOCUS_BORDER}`,
  cursor: "pointer",
  minHeight: 48,
  "&:hover": {
    backgroundColor: EPOCUS_PRIMARY_HOVER_BG,
  },
});

const viewCircleStyle = (checked: boolean): React.CSSProperties => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  border: checked ? "none" : `2px solid ${EPOCUS_BORDER}`,
  backgroundColor: checked ? EPOCUS_PRIMARY : "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: rem(12),
  color: EPOCUS_WHITE,
  transition: "all 0.15s ease",
});

export const ViewChecklist: FC<{
  views: { key: string; label: string }[];
  checked: Record<string, boolean>;
  onChange: (key: string, checked: boolean) => void;
  disabled?: boolean;
}> = ({ views, checked, onChange, disabled }) => (
  <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
    {views.map((v) => {
      const isChecked = checked[v.key] ?? false;
      return (
        <ViewRow
          key={v.key}
          onClick={() => !disabled && onChange(v.key, !isChecked)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (!disabled && (e.key === " " || e.key === "Enter")) {
              e.preventDefault();
              onChange(v.key, !isChecked);
            }
          }}
          tabIndex={disabled ? -1 : 0}
          role="checkbox"
          aria-checked={isChecked}
          aria-label={v.label}
          aria-disabled={disabled}
          style={{
            ...(isChecked
              ? {
                  borderColor: EPOCUS_PRIMARY,
                  backgroundColor: EPOCUS_SELECTED_BG,
                }
              : {}),
            ...(disabled ? { cursor: "default", opacity: 0.6 } : {}),
          }}
        >
          <Typography
            style={{
              fontSize: rem(14),
              fontWeight: isChecked ? 600 : 400,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {v.label}
          </Typography>
          <span style={viewCircleStyle(isChecked)}>{isChecked ? "✓" : ""}</span>
        </ViewRow>
      );
    })}
  </Box>
);

// ─── BooleanToggle ───────────────────────────────────────────────────────────

export const BooleanToggle: FC<{
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  labels?: { yes: string; no: string };
  disabled?: boolean;
}> = ({ value, onChange, labels = { yes: "Yes", no: "No" }, disabled }) => (
  <ChipRow>
    <Chip
      label={labels.no}
      variant="outlined"
      style={chipStyle(value === false)}
      onClick={() => onChange(value === false ? null : false)}
      disabled={disabled}
    />
    <Chip
      label={labels.yes}
      variant="outlined"
      style={chipStyle(value === true)}
      onClick={() => onChange(value === true ? null : true)}
      disabled={disabled}
    />
  </ChipRow>
);

// ─── NumericInput ────────────────────────────────────────────────────────────

export const NumericInput: FC<{
  value: number | null;
  onChange: (v: number | null) => void;
  unit: string;
  step?: number;
  label?: string;
  disabled?: boolean;
}> = ({ value, onChange, unit, step = 0.1, label, disabled }) => (
  <TextField
    type="number"
    value={value ?? ""}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      onChange(raw === "" ? null : Number(raw));
    }}
    variant="outlined"
    size="small"
    label={label}
    disabled={disabled}
    inputProps={{ step, "aria-label": label ?? unit, min: 0 }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            {unit}
          </Typography>
        </InputAdornment>
      ),
    }}
    style={{ maxWidth: 160 }}
  />
);

// ─── AllNormalToggle (switch with description) ───────────────────────────────

const AllNormalCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
});

const EpocusSwitch = styled(Switch)({
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: EPOCUS_PRIMARY,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: EPOCUS_PRIMARY,
  },
});

export const AllNormalToggle: FC<{
  isAllNormal: boolean;
  onToggle: () => void;
  disabled?: boolean;
}> = ({ isAllNormal, onToggle, disabled }) => (
  <AllNormalCard>
    <Box>
      <Typography
        style={{
          fontWeight: 700,
          fontSize: rem(15),
          color: EPOCUS_TEXT_PRIMARY,
        }}
      >
        All Normal
      </Typography>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        Mark all views obtained and all findings normal. Edit exceptions below.
      </Typography>
    </Box>
    <EpocusSwitch
      checked={isAllNormal}
      onChange={onToggle}
      disabled={disabled}
      inputProps={{ "aria-label": "All Normal" }}
    />
  </AllNormalCard>
);

// ─── WorksheetActions (Save Draft + Add to Review Queue) ─────────────────────

const SUBMIT_GREEN = "#16a34a";

export const WorksheetActions: FC<{
  isComplete: boolean;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onAssignToReviewer?: (reviewerId: string) => void;
  isSubmitted: boolean;
  disabled?: boolean;
}> = ({
  isComplete,
  onSaveDraft,
  onSubmit,
  onAssignToReviewer,
  isSubmitted,
  disabled,
}) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const submitDisabled = !!disabled || isSubmitted || !isComplete;

  const handlePick = (reviewerId: string) => {
    setPickerAnchor(null);
    onAssignToReviewer?.(reviewerId);
  };

  return (
    <Box display="flex" style={{ gap: 12 }}>
      <Button
        variant="outlined"
        onClick={onSaveDraft}
        disabled={disabled || isSubmitted}
        style={{
          flex: 1,
          minHeight: 48,
          textTransform: "none",
          fontWeight: 600,
          fontSize: rem(14),
          borderColor: EPOCUS_BORDER,
          color: EPOCUS_TEXT_PRIMARY,
        }}
      >
        Save Draft
      </Button>
      <Box display="flex" style={{ flex: 1, gap: 1 }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitDisabled}
          startIcon={isSubmitted ? <CheckCircleIcon /> : undefined}
          style={{
            flex: 1,
            minHeight: 48,
            textTransform: "none",
            fontWeight: 700,
            fontSize: rem(14),
            color: EPOCUS_WHITE,
            backgroundColor: isSubmitted ? SUBMIT_GREEN : EPOCUS_PRIMARY,
            borderTopRightRadius: onAssignToReviewer ? 0 : undefined,
            borderBottomRightRadius: onAssignToReviewer ? 0 : undefined,
          }}
        >
          {isSubmitted ? "Submitted" : "Add to Review Queue"}
        </Button>
        {onAssignToReviewer && (
          <Button
            variant="contained"
            onClick={(e) => setPickerAnchor(e.currentTarget)}
            disabled={submitDisabled}
            aria-haspopup="menu"
            aria-label="Assign to a specific reviewer"
            style={{
              minHeight: 48,
              minWidth: 44,
              padding: "0 8px",
              textTransform: "none",
              color: EPOCUS_WHITE,
              backgroundColor: isSubmitted ? SUBMIT_GREEN : EPOCUS_PRIMARY,
              borderLeft: `1px solid ${EPOCUS_WHITE}33`,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </Box>
      <Menu
        anchorEl={pickerAnchor}
        open={!!pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        getContentAnchorEl={null}
        PaperProps={{
          style: { minWidth: 240, borderRadius: 12, marginTop: -8 },
        }}
      >
        <Box style={{ padding: "8px 14px 4px" }}>
          <Typography
            style={{
              fontSize: rem(10),
              fontWeight: 700,
              color: EPOCUS_TEXT_SECONDARY,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Assign to reviewer
          </Typography>
        </Box>
        {REVIEWERS.map((r) => (
          <MenuItem
            key={r.id}
            onClick={() => handlePick(r.id)}
            style={{ paddingTop: 10, paddingBottom: 10 }}
          >
            <Box display="flex" flexDirection="column">
              <Typography
                style={{
                  fontSize: rem(13),
                  fontWeight: 600,
                  color: EPOCUS_TEXT_PRIMARY,
                }}
              >
                {r.name}
              </Typography>
              <Typography
                style={{ fontSize: rem(11), color: EPOCUS_TEXT_SECONDARY }}
              >
                {r.role}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

// ─── Responsive two-column layout ────────────────────────────────────────────

export const WorksheetColumns = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  [epocusTheme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
});

export const WorksheetColumnLeft = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  flex: 1,
});

export const WorksheetColumnRight = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  flex: 1,
});

// ─── NotesField ──────────────────────────────────────────────────────────────

export const NotesField: FC<{
  value: string | null;
  onChange: (v: string | null) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <TextField
    fullWidth
    multiline
    minRows={3}
    variant="outlined"
    placeholder="Additional clinical notes..."
    value={value ?? ""}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value || null)
    }
    disabled={disabled}
    inputProps={{ "aria-label": "Notes" }}
  />
);
